import moment from 'moment';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Lightbox from 'react-image-lightbox';
import { Tooltip } from 'react-tooltip';
import { useLazyGetExpenseForReportQuery, useLazyGetExpenseItemByExpenseIdQuery, useLazyGetExpenseItemsByExpenseItemIdQuery } from '../../../services/ExpenseApi';
import { useLazyGetReceiptVaultUploadRulesQuery } from '../../../services/authApi';
import { useGetAllDraftInvoicesAdminQuery, useLazyGetInvoiceDetailByIdQuery } from '../../../services/InvoiceDataDraftApi';
import ExpenseDetailModal from '../expenseItems/ExpenseDetailModal';
import { UpdateExpenseItemModal } from '../expenseItems/update-single-Item/UpdateExpenseItemModal';
import AddInvoiceModal from './AddInvoiceModal';
import UploadReceiptForUserModal from './UploadReceiptForUserModal';
import CompareOcrExpenseItemModal from './CompareOcrExpenseItemModal';
import ViewOcrDetailsModal from './ViewOcrDetailsModal';
import DeleteInvoiceModal from './DeleteInvoiceModal';
import RetryInvoiceModal from './RetryInvoiceModal';
import SingleItemDetailModal from '../expenseItems/update-single-Item/SingleItemDetailModal';
import heic2any from 'heic2any';
import Pagination from '../../../components/pagination/Pagination';
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent';
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent';
import TabularInvoices from './TabularInvoices';
import InvoiceDraftDetail from './InvoiceDraftDetail';
import InvoiceDraftFilterForm from './InvoiceDraftFilterForm';
import InvoiceGridList from './InvoiceGridList';
import { PageTitle, PageTitleActions } from '../../../_metronic/layout/core'
import { Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { constraintConfig } from '../../../constraintConfig'
import ExpenseItemViolationModal from '../expenseItems/ExpenseItemViolationModal'
import BetterLineItemMergeModal from '../expenseItems/BetterLineItemMergeModal'
import UnMergeConfirmationModal from '../expenseItems/UnMergeConfirmationModal'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import { canViewAnyUserReceipts, canViewOcrDiagnostics, getAllReceiptsPageTitle, getAttachedReceiptExpenseId, getEffectiveLineItemId } from './allReceiptsActionHelpers'
import labelKey from '../../localization/label.json'
import { API_DATE_FORMAT } from '../../../utils/dateLocale'
import SsHorizontalScroll from '../../../components/scroll/SsHorizontalScroll'

interface LightboxData {
    imageURL: string;
}

// Custom hook to manage modal visibility and auto-refetch on close
const useModalManager = (refetch: () => void) => {
    const [modals, setModals] = useState({
        addUpdateDepartment: false,
        attachmentDelete: false,
        retryInvoice: false,
        expenseDetail: false,
        updateExpenseItem: false,
        expenseItemDetail: false,
    });

    const prevModals = useRef(modals);

    useEffect(() => {
        const changed = Object.keys(modals).some(
            (key) => modals[key as keyof typeof modals] !== prevModals.current[key as keyof typeof modals]
        );
        if (changed) {
            Object.keys(modals).forEach((key) => {
                const k = key as keyof typeof modals;
                if (prevModals.current[k] && !modals[k]) {
                    refetch();
                }
            });
            prevModals.current = modals;
        }
    }, [modals, refetch]);

    const toggleModal = (key: keyof typeof modals, value?: boolean) => {
        setModals((prev) => ({ ...prev, [key]: value ?? !prev[key] }));
    };

    return { modals, toggleModal };
};

const AllInvoicesAdmin = () => {
    const { roleID, userAccountID } = useAppSelector(selectAuth);
    const showAnyUserReceipts = canViewAnyUserReceipts(roleID);
    const showOcrDiagnostics = canViewOcrDiagnostics(roleID);
    const [searchParams] = useSearchParams();
    const deepLinkDraftId = searchParams.get('invoiceDataDraftId')?.trim() || '';
    const currentDate = moment().format(API_DATE_FORMAT);
    const threeDaysAgo = moment().subtract(7, 'days').format(API_DATE_FORMAT);

    // UI state
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxData, setLightboxData] = useState<LightboxData>({ imageURL: '' });
    const [heicPreviewUrls, setHeicPreviewUrls] = useState<{ [key: string]: string }>({});
    const [viewMode, setViewMode] = useState<'grid' | 'tabular'>('tabular');
    const [showFilters, setShowFilters] = useState(false);
    const PAGE_INFO_SESSION_KEY = 'ss-allreceipts-info-dismissed';
    const [showPageInfo, setShowPageInfo] = useState(() => {
        try {
            return sessionStorage.getItem(PAGE_INFO_SESSION_KEY) !== '1';
        } catch {
            return true;
        }
    });
    const dismissPageInfo = useCallback(() => {
        setShowPageInfo(false);
        try {
            sessionStorage.setItem(PAGE_INFO_SESSION_KEY, '1');
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        if (!showPageInfo) return;
        const timer = window.setTimeout(() => {
            dismissPageInfo();
        }, 15000);
        return () => window.clearTimeout(timer);
    }, [showPageInfo, dismissPageInfo]);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
        deepLinkDraftId ? Number(deepLinkDraftId) || null : null
    );
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showDetailPanel, setShowDetailPanel] = useState(!!deepLinkDraftId);
    const [showUploadForUserModal, setShowUploadForUserModal] = useState(false);
    const [compareReceiptRow, setCompareReceiptRow] = useState<any>(null);
    const [ocrDetailRow, setOcrDetailRow] = useState<any>(null);
    const [violationExpenseItemId, setViolationExpenseItemId] = useState<number | null>(null);
    const [showViolationModal, setShowViolationModal] = useState(false);
    const [violationSourceData, setViolationSourceData] = useState<any>(null);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeSelectedItem, setMergeSelectedItem] = useState<any>(null);
    const [showUnmergeModal, setShowUnmergeModal] = useState(false);
    const [unmergeSelectedItem, setUnmergeSelectedItem] = useState<any>(null);
    const [viewOnlyData, setViewOnlyData] = useState(true);
    const [attachmentDeleteData, setAttachmentDeleteData] = useState<number | null>(null);
    const [invoiceDetailData, setInvoiceDetailData] = useState<any>(null);
    const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState('createdDT');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedExpenseItemId, setSelectedExpenseItemId] = useState<number | null>(null);

    // Filters — default status = All. Deep-link by SS Receipt ID clears other criteria.
    const [filters, setFilters] = useState({
        filterByStatus: String(constraintConfig.invoiceStatus.all),
        filterByStartDate: deepLinkDraftId ? "" : threeDaysAgo,
        filterByEndDate: deepLinkDraftId ? "" : currentDate,
        // Deep-link must not be scoped to impersonated/self user — find receipt by ID.
        filterByUserIds: deepLinkDraftId
            ? ""
            : showAnyUserReceipts
              ? ""
              : String(userAccountID || ""),
        searchTerm: "",
        filterByItemStartDate: "",
        filterByItemEndDate: "",
        filterByItemAmount: "",
        filterByReceiptStartDate: "",
        filterByReceiptEndDate: "",
        filterByReceiptInvoiceNo: "",
        filterByReceiptAmount: "",
        filterByItemSource: 0,
        filterByReceiptSource: 0,
        invoiceDataDraftId: deepLinkDraftId,
    });

    const activeStatusTab = parseInt(filters.filterByStatus || "0", 10) || 0;

    // Keep self-scoped user filter when auth loads / role cannot view all users
    // (skip while deep-linking by SS Receipt ID so the match is not hidden)
    useEffect(() => {
        if (deepLinkDraftId || filters.invoiceDataDraftId) return;
        if (showAnyUserReceipts || !userAccountID) return;
        setFilters((prev) => {
            const selfId = String(userAccountID);
            if (prev.filterByUserIds === selfId) return prev;
            return { ...prev, filterByUserIds: selfId };
        });
    }, [showAnyUserReceipts, userAccountID, deepLinkDraftId, filters.invoiceDataDraftId]);

    // API hooks
    const [triggerItemsById, { data: itemsByIdData }] = useLazyGetExpenseItemByExpenseIdQuery();
    const [triggerexpenseReportData, { data: expenseReportData }] = useLazyGetExpenseForReportQuery();
    const [triggerItemById, { data: itemByIdData, isFetching: itemByIdDataFetching }] =
        useLazyGetExpenseItemsByExpenseItemIdQuery();
    const [triggerInvoiceDetail] = useLazyGetInvoiceDetailByIdQuery();

    const { data, isLoading, isFetching, refetch, isSuccess: draftsLoaded } = useGetAllDraftInvoicesAdminQuery({
        pageSize,
        pageNumber: page,
        sortBy,
        sortOrder,
        searchStr: filters.searchTerm || undefined,
        receiptInvoiceNo: filters.filterByReceiptInvoiceNo || undefined,
        userIds: filters.filterByUserIds || undefined,
        filterByStatus: filters.filterByStatus !== "0" ? parseInt(filters.filterByStatus) : 0,
        startDate: filters.filterByStartDate || undefined,
        endDate: filters.filterByEndDate || undefined,
        itemStartDate: filters.filterByItemStartDate || undefined,
        itemEndDate: filters.filterByItemEndDate || undefined,
        itemAmount: filters.filterByItemAmount || undefined,
        receiptStartDate: filters.filterByReceiptStartDate || undefined,
        receiptEndDate: filters.filterByReceiptEndDate || undefined,
        receiptAmount: filters.filterByReceiptAmount || undefined,
        itemSource: filters.filterByItemSource || undefined,
        receiptSource: filters.filterByReceiptSource || undefined,
        invoiceDataDraftId: filters.invoiceDataDraftId || undefined,
    });

    const [prefetchReceiptVaultRules] = useLazyGetReceiptVaultUploadRulesQuery()
    // Prefetch once after list loads so Upload Receipt opens without waiting on rules
    useEffect(() => {
        if (!draftsLoaded) return
        prefetchReceiptVaultRules(undefined, true)
    }, [draftsLoaded, prefetchReceiptVaultRules])

    // Modal manager
    const { modals, toggleModal } = useModalManager(refetch);
    const {
        addUpdateDepartment: showAddUpdateDepartmentModal,
        attachmentDelete: showAttachmentDeleteModal,
        retryInvoice: showRetryInvoiceAttachmentModal,
        expenseDetail: showExpenseDetailModal,
        updateExpenseItem: showUpdateExpenseItemModal,
        expenseItemDetail: showExpenseItemDetailModal,
    } = modals;

    // HEIC conversion
    useEffect(() => {
        if (!data?.result?.data) return;
        const heicItems = data.result.data.filter(
            (item: any) =>
                item.fileURL &&
                /\.(heic|heif)$/i.test(item.fileURL) &&
                !heicPreviewUrls[item.invoiceDataDraftId]
        );

        heicItems.forEach(async (item: any) => {
            try {
                const response = await fetch(item.fileURL);
                const blob = await response.blob();
                const convertedBlob = await heic2any({ blob, toType: 'image/jpeg', quality: 0.8 });
                const reader = new FileReader();
                reader.onload = () => {
                    setHeicPreviewUrls((prev) => ({
                        ...prev,
                        [item.invoiceDataDraftId]: reader.result as string,
                    }));
                };
                reader.readAsDataURL(convertedBlob as Blob);
            } catch (error) {
                console.error('HEIC conversion error:', error);
            }
        });
    }, [data, heicPreviewUrls]);

    // Auto-refresh for pending OCR
    useEffect(() => {
        const interval = setInterval(() => {
            if (data?.result?.data?.some((item: any) => item.ocrReadStatus === 0)) {
                refetch();
            }
        }, 15000);
        return () => clearInterval(interval);
    }, [data, refetch]);

    // Handlers
    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        refetch().finally(() => setIsRefreshing(false));
    }, [refetch]);

    const openPdfInBrowserOrLightbox = useCallback(
        (attachmentFile: string | undefined) => {
            if (!attachmentFile) return;
            const isPdf = /\.pdf$/i.test(attachmentFile);
            const isMsgEml = /\.(msg|eml)$/i.test(attachmentFile);
            const isHeic = /\.(heic|heif)$/i.test(attachmentFile);

            if (isPdf || isMsgEml) {
                window.open(attachmentFile, '_blank');
            } else if (isHeic) {
                const draftId = data?.result?.data?.find(
                    (item: any) => item.fileURL === attachmentFile
                )?.invoiceDataDraftId;
                if (draftId && heicPreviewUrls[draftId]) {
                    setLightboxData({ imageURL: heicPreviewUrls[draftId] });
                    setLightboxOpen(true);
                } else {
                    window.open(attachmentFile, '_blank');
                }
            } else {
                setLightboxData({ imageURL: attachmentFile });
                setLightboxOpen(true);
            }
        },
        [data, heicPreviewUrls]
    );

    const handleApplyFilters = useCallback(
        (newFilters: any) => {
            setIsApplyingFilters(true);
            const next = { ...newFilters };
            const draftId = String(next.invoiceDataDraftId || "").trim();
            const trackingId = String(next.searchTerm || "").trim();
            // Exclusive ID lookups clear other criteria (form already does this; keep parent consistent)
            if (draftId || trackingId) {
                next.filterByStatus = String(constraintConfig.invoiceStatus.all);
                next.filterByStartDate = "";
                next.filterByEndDate = "";
                next.filterByItemStartDate = "";
                next.filterByItemEndDate = "";
                next.filterByItemAmount = "";
                next.filterByReceiptStartDate = "";
                next.filterByReceiptEndDate = "";
                next.filterByReceiptInvoiceNo = "";
                next.filterByReceiptAmount = "";
                next.filterByItemSource = 0;
                next.filterByReceiptSource = 0;
                if (draftId) {
                    next.invoiceDataDraftId = draftId;
                    next.searchTerm = "";
                } else {
                    next.searchTerm = trackingId;
                    next.invoiceDataDraftId = "";
                }
            }
            if (!showAnyUserReceipts && !(draftId || trackingId)) {
                next.filterByUserIds = String(userAccountID || "");
            } else if (draftId || trackingId) {
                next.filterByUserIds = "";
            }
            setFilters(next);
            setPage(1);
            if (typeof window !== 'undefined' && (window.innerWidth < 992 || window.matchMedia('(max-width: 991.98px)').matches)) {
                setShowFilters(false);
            }
            refetch().finally(() => setIsApplyingFilters(false));
        },
        [refetch, showAnyUserReceipts, userAccountID]
    );

    const handleStatusTabChange = useCallback(
        (status: number) => {
            setFilters((prev) => ({
                ...prev,
                filterByStatus: String(status),
                ...(showAnyUserReceipts ? {} : { filterByUserIds: String(userAccountID || "") }),
            }));
            setPage(1);
        },
        [showAnyUserReceipts, userAccountID]
    );

    const handlePageSizeChange = useCallback((e: any) => {
        setPageSize(parseInt(e.target.value));
        setPage(1);
    }, []);

    const handlePageClick = useCallback((e: any) => setPage(e.selected + 1), []);

    const handleSort = useCallback((columnId: string) => {
        setPage(1);
        setSortBy((prev) => {
            if (prev === columnId) {
                setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder('desc');
            return columnId;
        });
    }, []);

    const handleViewLineItem = useCallback(
        async (expenseItemId: number) => {
            if (!expenseItemId) return;
            // Set selected id first so modal ignores any previous row's cached result
            setSelectedExpenseItemId(expenseItemId);
            toggleModal('expenseItemDetail', true);
            try {
                await triggerItemById({ expenseItemId }).unwrap();
            } catch (error) {
                console.error('Failed to fetch line item:', error);
            }
        },
        [toggleModal, triggerItemById]
    );

    const handleCopyAttachmentUrl = useCallback(async (item: any) => {
        const url = item?.fileURL;
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Attachment URL copied');
        } catch {
            toast.error('Unable to copy attachment URL');
        }
    }, []);

    const handleViewViolations = useCallback((item: any) => {
        const itemId = Number(item?.expenseItemId || item?.usedExpenseItemId);
        if (!itemId) return;
        setViolationSourceData(item);
        setViolationExpenseItemId(itemId);
        setShowViolationModal(true);
    }, []);

    const handleMergeLineItem = useCallback((item: any) => {
        const expenseItemId = getEffectiveLineItemId(item);
        if (!expenseItemId) {
            toast.warning('No line item is linked to this receipt.');
            return;
        }
        setMergeSelectedItem({
            ...item,
            expenseItemId,
            amount: item.expenseItemAmount ?? item.amount,
            itemDate: item.expenseItemDate ?? item.invoiceDate,
            recordSourceId: item.lineItemRecordSourceId,
            // Receipt vault uses fileURL; expense items use attachmentFile — normalize for merge preview
            attachmentFile:
                item.attachmentFile ||
                item.fileURL ||
                item.expenseItemAttachmentFile ||
                item.invoiceDataDraft?.fileURL ||
                '',
            fileURL: item.fileURL || item.attachmentFile || '',
            invoiceDataDraftId: item.invoiceDataDraftId,
            invoiceNo: item.invoiceNo,
        });
        setShowMergeModal(true);
    }, []);

    const handleUnmergeLineItem = useCallback((item: any) => {
        if (!item?.expenseItemId) {
            toast.warning('No line item is linked to this receipt.');
            return;
        }
        if (!(Number(item?.mergeExpenseitemId) > 0)) {
            toast.warning('This line item is not merged.');
            return;
        }
        setUnmergeSelectedItem({
            ...item,
            expenseItemId: item.expenseItemId,
            amount: item.expenseItemAmount ?? item.amount,
            itemDate: item.expenseItemDate ?? item.invoiceDate,
            paymentMethod: item.paymentMethod || item.expensePayment,
            attachmentFile:
                item.attachmentFile ||
                item.fileURL ||
                item.expenseItemAttachmentFile ||
                '',
            fileURL: item.fileURL || item.attachmentFile || '',
        });
        setShowUnmergeModal(true);
    }, []);

    const handleEditLineItem = useCallback(
        async (item: any) => {
            const expenseItemId = Number(item?.expenseItemId || 0);
            if (!expenseItemId) {
                toast.warning('No line item is linked to this receipt.');
                return;
            }
            setViewOnlyData(false);
            toggleModal('updateExpenseItem', true);
            try {
                await triggerItemById({ expenseItemId }).unwrap();
            } catch (error) {
                console.error('Failed to fetch line item for edit:', error);
                toast.error('Unable to load line item for edit');
                toggleModal('updateExpenseItem', false);
                setViewOnlyData(true);
            }
        },
        [toggleModal, triggerItemById]
    );

    const handleViewReport = useCallback((item: any) => {
        const expenseId = getAttachedReceiptExpenseId(item) || (Number(item?.expenseId) > 0 ? Number(item.expenseId) : 0);
        if (!expenseId) {
            toast.warning('No expense report is linked to this receipt.');
            return;
        }
        toggleModal('expenseDetail', true);
        triggerexpenseReportData({ expenseId });
        triggerItemsById({ ExpenseId: expenseId });
    }, [toggleModal, triggerexpenseReportData, triggerItemsById]);

    const handleViewOcrText = useCallback(async (row: any) => {
        const id = row?.invoiceDataDraftId;
        if (!id) {
            setOcrDetailRow(row);
            return;
        }
        try {
            const result = await triggerInvoiceDetail(id).unwrap();
            setOcrDetailRow(result?.result || row);
        } catch {
            setOcrDetailRow(row);
        }
    }, [triggerInvoiceDetail]);

    const toggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);

    const handleRowClick = useCallback(
        async (item: any) => {
            const id = item?.invoiceDataDraftId;
            if (!id) return;
            setShowFilters(false);
            setSelectedInvoiceId(id);
            // Show list row immediately so the detail pane is never blank while fetching
            setInvoiceDetailData(item);
            if (!showDetailPanel) setShowDetailPanel(true);
            setInvoiceDetailLoading(true);
            try {
                const result = await triggerInvoiceDetail(id).unwrap();
                const detail = result?.result ?? result?.Result ?? null;
                if (detail) setInvoiceDetailData(detail);
            } catch (error) {
                console.error('Failed to fetch invoice detail:', error);
                // Keep list-row data so detail view stays usable
            } finally {
                setInvoiceDetailLoading(false);
            }
        },
        [showDetailPanel, triggerInvoiceDetail]
    );

    // Deep-link from OCR support emails: /allreceipts?invoiceDataDraftId=123
    // Always load receipt detail by ID (do not wait for list match — filters/user scope can hide the row).
    useEffect(() => {
        if (!deepLinkDraftId) return;
        const id = Number(deepLinkDraftId);
        if (!id) return;

        let cancelled = false;
        setShowDetailPanel(true);
        setShowFilters(true);
        setSelectedInvoiceId(id);
        setInvoiceDetailLoading(true);

        (async () => {
            try {
                const result = await triggerInvoiceDetail(id).unwrap();
                if (!cancelled) {
                    setInvoiceDetailData(result?.result || null);
                }
            } catch (error) {
                console.error('Failed to fetch deep-linked invoice detail:', error);
                if (!cancelled) setInvoiceDetailData(null);
            } finally {
                if (!cancelled) setInvoiceDetailLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [deepLinkDraftId, triggerInvoiceDetail]);

    // Once list data includes the deep-linked row, highlight/select it in the grid
    useEffect(() => {
        if (!deepLinkDraftId || !data?.result?.data?.length) return;
        const match = data.result.data.find(
            (item: any) => String(item?.invoiceDataDraftId) === String(deepLinkDraftId)
        );
        if (match && selectedInvoiceId !== match.invoiceDataDraftId) {
            setSelectedInvoiceId(match.invoiceDataDraftId);
        }
    }, [deepLinkDraftId, data, selectedInvoiceId]);

    const handleDelete = useCallback((id: number) => {
        setAttachmentDeleteData(id);
        toggleModal('attachmentDelete', true);
    }, [toggleModal]);

    const handleRetry = useCallback((id: number) => {
        setAttachmentDeleteData(id);
        toggleModal('retryInvoice', true);
    }, [toggleModal]);

    const handleCloseDetail = useCallback(() => {
        setSelectedInvoiceId(null);
        setShowDetailPanel(false);
        setInvoiceDetailData(null);
    }, []);

    const toggleDetailPanel = useCallback(() => {
        const newState = !showDetailPanel;
        setShowDetailPanel(newState);
        if (newState) {
            setShowFilters(false);
            if (!selectedInvoiceId) {
                const firstItem = data?.result?.data?.[0];
                if (firstItem) handleRowClick(firstItem);
            }
        }
    }, [showDetailPanel, selectedInvoiceId, data, handleRowClick]);

    // Memoized data
    const invoiceList = useMemo(() => data?.result?.data || [], [data]);
    const totalRecords = data?.result?.totalRecords || 0;
    const totalPages = data?.result?.totalPages || 0;
    const pageNumber = data?.result?.pageNumber || 1;

    const handleSearchChange = useCallback((e: any) => {
        const value = e.target.value;
        setFilters((prev: any) => ({
            ...prev,
            searchTerm: value,
            invoiceDataDraftId: value ? "" : prev.invoiceDataDraftId,
        }));
        setPage(1);
    }, []);

    // Render helpers
    const renderFilterToggleButton = () => (
        <button
            type="button"
            className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active flex-shrink-0"
            onClick={toggleFilters}
        >
            <i className="fa fa-filter me-1 text-white"></i>
            <span className="ss-filter-toggle-label-desktop">Show Filters</span>
            <span className="ss-filter-toggle-label-mobile">Filters</span>
        </button>
    );

    const handleSwitchViewMode = useCallback((newMode: 'grid' | 'tabular') => {
        if (newMode === viewMode) return;
        setViewMode(newMode);
        if (newMode === 'grid') {
            handleCloseDetail();
        }
    }, [viewMode, handleCloseDetail]);

    const renderViewModeButtons = useMemo(() => (
        <div className="d-inline-flex align-items-center border rounded overflow-hidden shadow-sm bg-body flex-shrink-0 ss-view-mode-toggle ms-auto">
            <button
                type="button"
                className={`btn btn-sm text-nowrap ${viewMode === 'tabular' ? 'btn-primary' : 'btn-outline-secondary'} rounded-0 border-0 px-3 py-1.5 fs-7`}
                onClick={() => handleSwitchViewMode('tabular')}
            >
                <i className={`fa fa-list me-1.5 ${viewMode === 'tabular' ? 'text-white' : 'text-primary'}`}></i>
                List View
            </button>

            <button
                type="button"
                className={`btn btn-sm text-nowrap ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'} rounded-0 border-0 border-start px-3 py-1.5 fs-7`}
                onClick={() => handleSwitchViewMode('grid')}
            >
                <i className={`fa fa-th-large me-1.5 ${viewMode === 'grid' ? 'text-white' : 'text-primary'}`}></i>
                Grid View
            </button>
        </div>
    ), [viewMode, handleSwitchViewMode]);

    return (
        <>
            {/* Modals */}
            <ExpenseDetailModal
                show={showExpenseDetailModal}
                handleClose={() => toggleModal('expenseDetail', false)}
                data={expenseReportData?.result}
                expenseItems={itemsByIdData?.result}
                refetch={refetch}
            />
            <UpdateExpenseItemModal
                show={showUpdateExpenseItemModal}
                handleClose={() => {
                    toggleModal('updateExpenseItem', false);
                    setViewOnlyData(true);
                }}
                data={itemByIdData?.result}
                viewOnlyData={viewOnlyData}
                refetch={refetch}
            />
            <SingleItemDetailModal
                key={selectedExpenseItemId || 'line-item-detail'}
                show={showExpenseItemDetailModal}
                handleClose={() => {
                    toggleModal('expenseItemDetail', false);
                    setSelectedExpenseItemId(null);
                }}
                data={
                    selectedExpenseItemId &&
                    itemByIdData?.result &&
                    Number(itemByIdData.result.expenseItemId) === Number(selectedExpenseItemId)
                        ? itemByIdData.result
                        : null
                }
                viewOnlyData={true}
                isFetching={itemByIdDataFetching}
            />
            <DeleteInvoiceModal
                show={showAttachmentDeleteModal}
                handleClose={() => toggleModal('attachmentDelete', false)}
                data={attachmentDeleteData}
            />
            <RetryInvoiceModal
                show={showRetryInvoiceAttachmentModal}
                handleClose={() => toggleModal('retryInvoice', false)}
                data={attachmentDeleteData}
            />
            <AddInvoiceModal
                show={showAddUpdateDepartmentModal}
                handleClose={() => toggleModal('addUpdateDepartment', false)}
                data={{}}
            />
            <UploadReceiptForUserModal
                show={showUploadForUserModal}
                handleClose={() => {
                    setShowUploadForUserModal(false);
                    refetch();
                }}
            />
            <CompareOcrExpenseItemModal
                show={!!compareReceiptRow}
                handleClose={() => setCompareReceiptRow(null)}
                receiptRow={compareReceiptRow}
            />
            <ViewOcrDetailsModal
                show={!!ocrDetailRow}
                handleClose={() => setOcrDetailRow(null)}
                data={ocrDetailRow}
                onViewLineItem={(expenseItemId) => {
                    setOcrDetailRow(null);
                    handleViewLineItem(expenseItemId);
                }}
                onViewReport={(expenseId) => {
                    setOcrDetailRow(null);
                    toggleModal('expenseDetail', true);
                    triggerexpenseReportData({ expenseId });
                    triggerItemsById({ ExpenseId: expenseId });
                }}
            />

            <Tooltip id="refreshData" place="top" />
            <Tooltip id="retryInvoice" place="top" />
            <Tooltip id="reason" place="top" />
            <Tooltip id="reportId" place="top" />
            <Tooltip id="lineItem" place="top" />
            <Tooltip id="clickReceipt" place="top" />

            {lightboxOpen && (
                <Lightbox
                    mainSrc={lightboxData.imageURL}
                    onCloseRequest={() => setLightboxOpen(false)}
                    imageCaption="Attachment"
                    enableZoom={true}
                    imagePadding={50}
                />
            )}
            <PageTitle breadcrumbs={[]}>{getAllReceiptsPageTitle(roleID)}</PageTitle>
            <PageTitleActions>{renderViewModeButtons}</PageTitleActions>

            <div className="row gx-2 gy-2 ss-all-receipts-page-row page-container">
                <div className={showDetailPanel ? 'col-lg-8' : 'col-lg-12'}>
                    <div className={`card ss-listing-card ss-all-receipts-card ${showDetailPanel ? 'ss-all-receipts-card--with-detail' : ''} ${viewMode === 'grid' ? 'ss-all-receipts-card--grid-mode' : ''}`}>
                        <div className="card-body py-3 ss-listing-card-body">
                            <div className="ss-all-receipts-toolbar">
                            {/* <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 ss-all-receipts-card-title-row">
                                <h1 className="ss-all-receipts-card-title mb-0">
                                    {getAllReceiptsPageTitle(roleID)}
                                </h1>
                            </div> */}
                            {showPageInfo && (
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-2 ss-allreceipts-info-row">
                                    <Alert
                                        variant="primary"
                                        className="mb-0 py-2 ss-page-info-alert"
                                    >
                                        <div className="ss-page-info-alert__body">
                                            <p className="fs-7 mb-0">
                                                {showAnyUserReceipts
                                                    ? 'View and manage all uploaded receipts. Search by user, tracking ID, vendor, or status; review OCR and expense details; retry failed processing.'
                                                    : 'View and manage your uploaded receipts. Filter by status and dates, review OCR and expense details, and upload receipts for yourself.'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-active-color-primary ss-page-info-alert__close"
                                            aria-label="Close"
                                            onClick={dismissPageInfo}
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    </Alert>
                                </div>
                            )}
                            {/* Status tabs + view/actions */}
                            <div className="py-1">
                                <div className="d-flex flex-column flex-lg-row flex-wrap align-items-stretch align-items-lg-center justify-content-between gap-2 w-100 ss-onboarding-toolbar ss-receipt-vault-toolbar">
                                    <div className="d-flex gap-2 align-items-center min-w-0 w-100 w-lg-auto ss-receipt-vault-toolbar__tabs">
                                        <SsHorizontalScroll
                                            className="flex-grow-1 min-w-0 w-100 w-lg-auto"
                                            viewportClassName="status-tabs"
                                            ariaLabel="Receipt status tabs"
                                        >
                                        <div className="ss-user-tab-group btn-group" role="group" aria-label="Receipt status tabs" style={{ minWidth: 'max-content' }}>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.all ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.all)}
                                            >
                                                {labelKey.all}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === 0 ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(0)}
                                            >
                                                {labelKey.all} (Except Attached & Submitted, unless Drafted/Rejected)
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.readyToUse ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.readyToUse)}
                                            >
                                                {labelKey.readyToUse}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.attached ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.attached)}
                                            >
                                                {labelKey.attached}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.submitted ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.submitted)}
                                            >
                                                {labelKey.submitted}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.failed ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.failed)}
                                            >
                                                {labelKey.failed}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.alreadyUsed ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.alreadyUsed)}
                                            >
                                                {labelKey.alreadyUsed}
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm text-nowrap ${activeStatusTab === constraintConfig.invoiceStatus.futureReceipt ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleStatusTabChange(constraintConfig.invoiceStatus.futureReceipt)}
                                            >
                                                {labelKey.futureReceipt}
                                            </button>
                                        </div>
                                        </SsHorizontalScroll>
                                    </div>

                                    <div className={`ss-mobile-scroll-tabs w-100 w-lg-auto ss-receipt-vault-toolbar__actions ${showDetailPanel ? 'me-auto' : 'ms-lg-auto'}`}>
                                    <div className={`d-flex justify-content-start align-items-center flex-nowrap gap-2 ss-user-list-toolbar__actions ${showDetailPanel ? 'me-auto' : 'ms-lg-auto'}`} style={{ minWidth: 'max-content' }}>
                                        <SearchBarComponent
                                            placeholder="Receipt"
                                            value={filters.searchTerm || ''}
                                            onChange={handleSearchChange}
                                            className="ss-user-toolbar-search-input"
                                            mainClassName="ss-table-search--sm my-0"
                                        />
                                        <RefreshComponent onClick={handleRefresh} isLoading={isRefreshing} />
                                        {!showFilters && renderFilterToggleButton()}
                                        {showFilters && (
                                            <button
                                                type="button"
                                                className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap flex-shrink-0"
                                                onClick={toggleFilters}
                                            >
                                                <i className="fa fa-eye-slash me-1 text-white"></i>
                                                <span className="ss-filter-toggle-label-desktop">Hide Filters</span>
                                                <span className="ss-filter-toggle-label-mobile">Filters</span>
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active text-nowrap flex-shrink-0"
                                            onClick={() => {
                                                if (showAnyUserReceipts) {
                                                    setShowUploadForUserModal(true);
                                                } else {
                                                    toggleModal('addUpdateDepartment', true);
                                                }
                                            }}
                                        >
                                            <i className="fa fa-plus me-1 text-white" />
                                            Upload Receipt
                                        </button>
                                    </div>
                                    </div>
                                </div>
                            </div>

                            {showFilters && (
                                <div className="mt-1 mb-1 border-top">
                                    <InvoiceDraftFilterForm
                                        onApplyFilters={handleApplyFilters}
                                        isApplyingFilters={isApplyingFilters}
                                        initialFilters={filters}
                                        showUserFilter={showAnyUserReceipts}
                                        showStatusFilter={false}
                                        lockedUserId={
                                            deepLinkDraftId || filters.invoiceDataDraftId
                                                ? undefined
                                                : showAnyUserReceipts
                                                  ? undefined
                                                  : String(userAccountID || '')
                                        }
                                    />
                                </div>
                            )}
                            </div>

                            {/* Results */}
                            <div className="ss-all-receipts-results pt-1">
                                {viewMode === 'grid' ? (
                                    <div className="ss-all-receipts-grid-scroll">
                                        <InvoiceGridList
                                            data={invoiceList}
                                            isLoading={isLoading || isFetching}
                                            selectedInvoiceId={selectedInvoiceId}
                                            onRowClick={handleRowClick}
                                            onDelete={handleDelete}
                                            onRetry={handleRetry}
                                            onViewFile={openPdfInBrowserOrLightbox}
                                            heicPreviewUrls={heicPreviewUrls}
                                            showUserDetails={showAnyUserReceipts}
                                            isDetailPanelOpen={showDetailPanel}
                                            onViewReport={handleViewReport}
                                            onViewLineItem={handleViewLineItem}
                                            onCompareOcr={showOcrDiagnostics ? (row: any) => setCompareReceiptRow(row) : undefined}
                                            onViewOcrText={showOcrDiagnostics ? handleViewOcrText : undefined}
                                            onCopyAttachmentUrl={showOcrDiagnostics ? handleCopyAttachmentUrl : undefined}
                                            onViewViolations={handleViewViolations}
                                            onMergeLineItem={handleMergeLineItem}
                                            onUnmergeLineItem={handleUnmergeLineItem}
                                            onEditLineItem={handleEditLineItem}
                                        />
                                    </div>
                                ) : (
                                    <TabularInvoices
                                        data={invoiceList}
                                        isLoading={isLoading || isFetching}
                                        sortBy={sortBy}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        showUserColumn={showAnyUserReceipts}
                                        onTriggerExpenseReport={(expenseId: number) =>
                                            triggerexpenseReportData({ expenseId })
                                        }
                                        onTriggerItemsById={(expenseId: number) =>
                                            triggerItemsById({ ExpenseId: expenseId })
                                        }
                                        onTriggerItemById={handleViewLineItem}
                                        onCompareOcrLineItem={showOcrDiagnostics ? (row: any) => setCompareReceiptRow(row) : undefined}
                                        onViewOcrText={showOcrDiagnostics ? handleViewOcrText : undefined}
                                        onCopyAttachmentUrl={showOcrDiagnostics ? handleCopyAttachmentUrl : undefined}
                                        onViewViolations={handleViewViolations}
                                        onMergeLineItem={handleMergeLineItem}
                                        onUnmergeLineItem={handleUnmergeLineItem}
                                        onEditLineItem={handleEditLineItem}
                                        setShowExpenseItemDetailModal={(show: boolean) => {
                                            if (!show) {
                                                toggleModal('expenseItemDetail', false);
                                                setSelectedExpenseItemId(null);
                                            }
                                        }}
                                        setAttachmentDeleteData={setAttachmentDeleteData}
                                        retryInvoiceHandler={() =>
                                            toggleModal('retryInvoice', true)
                                        }
                                        expenseDetailHandler={() =>
                                            toggleModal('expenseDetail', true)
                                        }
                                        onRowClick={handleRowClick}
                                        selectedRowId={selectedInvoiceId}
                                        onDelete={handleDelete}
                                    />
                                )}

                                {/* Pagination — always visible footer */}
                                <div className="ss-table-pagination-wrap">
                                    <Pagination
                                        totalResult={
                                            totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
                                        }
                                        toResult={
                                            totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)
                                        }
                                        ofResult={totalRecords}
                                        onChange={handlePageSizeChange}
                                        pageSize={pageSize}
                                        pageSizeOptions={[10, 25, 50, 100, 200, 500]}
                                        onPageChange={handlePageClick}
                                        pageCount={Math.max(totalPages || 0, 1)}
                                        currentPage={page}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {showDetailPanel && (
                    <div className="col-lg-4">
                        <InvoiceDraftDetail
                            data={invoiceDetailData}
                            isLoading={invoiceDetailLoading}
                            onClose={handleCloseDetail}
                            onImageClick={openPdfInBrowserOrLightbox}
                            onRetry={() => handleRetry(invoiceDetailData?.invoiceDataDraftId)}
                            onViewReport={() => {
                                const expenseId =
                                    getAttachedReceiptExpenseId(invoiceDetailData) ||
                                    (Number(invoiceDetailData?.expenseId) > 0
                                        ? Number(invoiceDetailData.expenseId)
                                        : 0);
                                if (!expenseId) return;
                                toggleModal('expenseDetail', true);
                                triggerexpenseReportData({ expenseId });
                                triggerItemsById({ ExpenseId: expenseId });
                            }}
                            onViewLineItem={handleViewLineItem}
                            onCompareOcrLineItem={showOcrDiagnostics ? () => setCompareReceiptRow(invoiceDetailData) : undefined}
                            showOcrDiagnostics={showOcrDiagnostics}
                            onCopyAttachmentUrl={async () => {
                                await handleCopyAttachmentUrl(invoiceDetailData);
                            }}
                            onViewViolations={() => handleViewViolations(invoiceDetailData)}
                            onMergeLineItem={() => handleMergeLineItem(invoiceDetailData)}
                            onUnmergeLineItem={() => handleUnmergeLineItem(invoiceDetailData)}
                            onEditLineItem={() => handleEditLineItem(invoiceDetailData)}
                        />
                    </div>
                )}
            </div>
            <ExpenseItemViolationModal
                show={showViolationModal}
                handleClose={() => {
                    setShowViolationModal(false);
                    setViolationExpenseItemId(null);
                    setViolationSourceData(null);
                }}
                data={(violationSourceData || invoiceDetailData) ? {
                    ...(violationSourceData || invoiceDetailData),
                    title: (violationSourceData || invoiceDetailData).reportTitle || (violationSourceData || invoiceDetailData).title,
                    trackingId: (violationSourceData || invoiceDetailData).trackingId,
                    expenseType: (violationSourceData || invoiceDetailData).expenseType,
                    expenseId: (violationSourceData || invoiceDetailData).expenseId,
                } : null}
                expenseItemId={violationExpenseItemId}
                refetch={refetch}
            />
            <BetterLineItemMergeModal
                show={showMergeModal}
                handleClose={() => {
                    setShowMergeModal(false);
                    setMergeSelectedItem(null);
                }}
                selectedDraftData={mergeSelectedItem}
                refetch={refetch}
            />
            <UnMergeConfirmationModal
                show={showUnmergeModal}
                onClose={() => {
                    setShowUnmergeModal(false);
                    setUnmergeSelectedItem(null);
                }}
                selectedDraftData={unmergeSelectedItem}
                refetch={() => refetch()}
                onSuccess={() => {
                    refetch();
                    if (selectedInvoiceId && invoiceDetailData?.invoiceDataDraftId === selectedInvoiceId) {
                        triggerInvoiceDetail(selectedInvoiceId)
                            .unwrap()
                            .then((result: any) => setInvoiceDetailData(result?.result || null))
                            .catch(() => undefined);
                    }
                }}
            />
        </>
    );
};

export default AllInvoicesAdmin;